import React, { useState, useEffect } from "react"
import {
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material"
import { useAuthUser } from "react-auth-kit"
import { Link } from "react-router-dom"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import { keyframes } from "@emotion/react"
import api from "../../../../utils/api"

const blink = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
`

type NotificationSnackbarProps = {
  message: string | null
  onClose: () => void
  severity: "success" | "error" | "warning" | "info"
}

const NotificationSnackbar: React.FC<NotificationSnackbarProps> = ({
  message,
  onClose,
  severity,
}) => (
  <Snackbar
    open={!!message}
    autoHideDuration={3000}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert onClose={onClose} severity={severity} sx={{ width: "100%" }}>
      {message}
    </Alert>
  </Snackbar>
)

type VotingInstanceCardProps = {
  instance: any
  setVotingInstances: React.Dispatch<React.SetStateAction<any[]>>
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string | null>>
  counter: number
}

const VotingInstanceCard: React.FC<VotingInstanceCardProps> = ({
  instance,
  setVotingInstances,
  setSnackbarMessage,
  counter,
}) => {
  const theme = useTheme()

  const publishInstance = async () => {
    try {
      await api.put(`/admin/instances/${instance.id}/publish`)

      // Update the local state to reflect that the instance has been published.
      setVotingInstances((prevInstances) =>
        prevInstances.map((inst) =>
          inst.id === instance.id ? { ...inst, status: "published" } : inst
        )
      )
    } catch (error) {
      console.error("Failed to publish the instance:", error)
    }
  }

  const handleStartButtonClick = async () => {
    if (instance.questionNum === 0) {
      setSnackbarMessage("Need at least one Question to start this instance!")
      return
    }

    try {
      await api.put(`/admin/instances/${instance.id}/start`)

      setVotingInstances((prevInstances) =>
        prevInstances.map((inst) =>
          inst.id === instance.id ? { ...inst, status: "open" } : inst
        )
      )
    } catch (err) {
      const errorMessage = err.message || "Failed to start instance"
      setSnackbarMessage(errorMessage)
    }
  }

  const handlePublishButtonClick = async () => {
    try {
      await api.put(`/admin/instances/${instance.id}/publish-results`)

      setVotingInstances((prevInstances) =>
        prevInstances.map((inst) =>
          inst.id === instance.id ? { ...inst, status: "published" } : inst
        )
      )
    } catch (err) {
      const errorMessage = err.message || "Failed to publish results"
      setSnackbarMessage(errorMessage)
    }
  }

  let buttonElement
  if (instance.status === "open") {
    buttonElement = (
      <Button
        variant="contained"
        color="primary"
        component={Link}
        to={`/admin/event-admin/instance/${instance.id}/live`}
      >
        Live Voting
      </Button>
    )
  } else if (instance.status === "published") {
    buttonElement = (
      <React.Fragment>
        <div>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${instance.id}/results`}
            sx={{ mt: 2, mr: 1 }}
          >
            View Results
          </Button>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${instance.id}/voters`}
            sx={{ mt: 2, mr: 1 }}
          >
            View Voters
          </Button>
        </div>
      </React.Fragment>
    )
  } else if (instance.status === "closed") {
    buttonElement = (
      <React.Fragment>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePublishButtonClick}
        >
          Publish Results
        </Button>
        <div>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${instance.id}/results`}
            sx={{ mt: 2, mr: 1 }}
          >
            View Results
          </Button>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${instance.id}/voters`}
            sx={{ mt: 2, mr: 1 }}
          >
            View Voters
          </Button>
        </div>
      </React.Fragment>
    )
  } else if (instance.status === "pre-voting") {
    buttonElement = (
      <Button
        variant="contained"
        color="primary"
        onClick={handleStartButtonClick}
      >
        Start
      </Button>
    )
  }

  return (
    <Card
      key={instance.id}
      sx={{
        m: 2,
        width: "300px",
        border: `1px solid ${theme.palette.primary.main}`,
        padding: "20px",
      }}
    >
      <Link
        to={`/admin/event-admin/instance/${instance.id}`}
        style={{ textDecoration: "none" }}
      >
        <CardContent sx={{ padding: "0" }}>
          <Typography variant="h6" style={{ color: "white" }}>
            {`#${counter + 1} - ${instance.description}`}
            {instance.status === "open" && (
              <FiberManualRecordIcon
                color="secondary"
                sx={{
                  marginLeft: "10px",
                  color: "red",
                  fontSize: "small",
                  animation:
                    instance.status === "open"
                      ? `${blink} 1.5s infinite`
                      : "none",
                }}
              />
            )}
          </Typography>
          <Typography variant="body2" style={{ color: "white" }}>
            {`Status: ${instance.status}`}
          </Typography>
        </CardContent>
      </Link>
      {buttonElement}
      <Button
        variant="contained"
        color="primary"
        component={Link}
        to={`/admin/event-admin/instance/${instance.id}`}
        style={{ margin: "10px" }}
      >
        View Instance
      </Button>
    </Card>
  )
}

const useVotingInstances = (eventId: string) => {
  const [votingInstances, setVotingInstances] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchVotingInstances = async () => {
      setIsLoading(true)
      try {
        const response = await api.get(
          `/admin/events/${eventId}/voting-instances`
        )

        const responseData = response.data

        const sortedInstances = [...responseData].sort((a: any, b: any) => {
          const aSeconds = a.createdAt?._seconds || 0
          const bSeconds = b.createdAt?._seconds || 0

          return aSeconds - bSeconds
        })

        setVotingInstances(sortedInstances)
      } catch (err) {
        setError(err.message)
        console.error("Error fetching voting instances:", err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchVotingInstances()
  }, [eventId])

  return { votingInstances, setVotingInstances, isLoading, error }
}

// create a counter
const StartInstancePage: React.FC<Props> = ({ notification = "" }) => {
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(
    notification
  )
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("error")
  const auth = useAuthUser()
  const eventRef = auth()?.eventId ?? ""
  const eventId = eventRef._path?.segments[1] ?? ""
  const { votingInstances, isLoading, setVotingInstances } =
    useVotingInstances(eventId)

  // State for the Voter Report dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [reportFileName, setReportFileName] = useState("")
  const [reportLoading, setReportLoading] = useState(false)
  const [eventName, setEventName] = useState("")

  // Fetch event name when component mounts
  useEffect(() => {
    const fetchEventName = async () => {
      if (!eventId) return
      try {
        const response = await api.get(`/admin/events/${eventId}`)
        const name = response.data.name || "Event"
        setEventName(name)
        // Create a sanitized default filename
        const sanitizedName = name.replace(/[^a-zA-Z0-9]/g, '_')
        const dateStr = new Date().toISOString().split('T')[0]
        setReportFileName(`${sanitizedName}_voters_${dateStr}`)
      } catch (error) {
        console.error('Error fetching event name:', error)
      }
    }

    fetchEventName()
  }, [eventId])

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true)
  }

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false)
  }

  const handleCreateReport = async () => {
    setReportLoading(true)
    try {
      // Get currently logged-in voters for this event
      const response = await api.get(`/admin/events/${eventId}/logged-in-voters`)
      const voters = response.data || []

      if (voters.length === 0) {
        setSnackbarMessage("No voters are currently logged in.")
        setSnackbarSeverity("info")
        setReportLoading(false)
        setReportDialogOpen(false)
        return
      }

      // Create CSV content
      const headers = ['Username', 'Name']
      const rows = voters.map((voter: any) => [
        voter.username || 'N/A',
        voter.name || 'Unknown'
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map((row: string[]) => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      // Sanitize filename if it was changed by the user
      const sanitizedFileName = reportFileName.replace(/[^a-zA-Z0-9_\-]/g, '_')
      
      // Create and trigger download
      const fileName = `${sanitizedFileName}.csv`
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up and close dialog
      URL.revokeObjectURL(link.href)
      setReportDialogOpen(false)
      setSnackbarMessage(`Voter report "${fileName}" has been created successfully.`)
      setSnackbarSeverity("success")
    } catch (error) {
      console.error('Error creating voter report:', error)
      setSnackbarMessage("Failed to create voter report. Please try again.")
      setSnackbarSeverity("error")
    } finally {
      setReportLoading(false)
    }
  }

  const renderVotingInstances = () => (
    <div
      style={{ display: "flex", justifyContent: "center", flexWrap: "wrap" }}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        [...votingInstances].map((instance, index) => (
          <VotingInstanceCard
            key={instance.id}
            instance={instance}
            setVotingInstances={setVotingInstances}
            setSnackbarMessage={setSnackbarMessage}
            counter={index}
          />
        ))
      )}
    </div>
  )

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="90vh"
      paddingBottom={4}
    >
      <Typography variant="h5" gutterBottom>
        Available Instances to Start
      </Typography>

      <Box display="flex" flexWrap="wrap" justifyContent="center" mb={2}>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={`/admin/event-admin/instance/create`}
          style={{ marginTop: "10px", marginRight: "10px" }}
          // disabled={isAnyInstanceOpen}
        >
          Create Instance
        </Button>

        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={`/admin/event-admin/instance/edit`}
          style={{ marginTop: "10px", marginRight: "10px" }}
          //disabled={isAnyInstanceOpen}
        >
          Edit Instance
        </Button>

        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={`/admin/event-admin/instance/delete`}
          style={{ marginTop: "10px", marginRight: "10px" }}
          //disabled={isAnyInstanceOpen}
        >
          Delete Instance
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={handleOpenReportDialog}
          style={{ marginTop: "10px", marginRight: "10px" }}
        >
          Voter Report
        </Button>
      </Box>

      {renderVotingInstances()}
      <NotificationSnackbar
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        severity={snackbarSeverity}
      />

      {/* Voter Report Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={handleCloseReportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Voter Report</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a CSV report of voters currently logged into this event. 
            This report will include their usernames and names.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            fullWidth
            value={reportFileName}
            onChange={(e) => setReportFileName(e.target.value)}
            variant="outlined"
            helperText="CSV extension will be added automatically"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseReportDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleCreateReport} 
            color="primary" 
            disabled={reportLoading || !reportFileName.trim()}
            variant="contained"
            startIcon={reportLoading ? <CircularProgress size={20} /> : null}
          >
            {reportLoading ? "Creating..." : "Create Report"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

type Props = {
  notification?: string
}

export default StartInstancePage
